/**
 * @typedef {import("@/managers/DesignFile").default} DesignFile
 * @typedef {import("@/entities/SiteDesign").default} SiteDesign

/**
 * Utility functions for handling page save failures and cleanup operations
 */
class PageCleanupUtils {
  /**
   * Handles cleanup when a page save fails
   * @param {Object} params - Cleanup parameters
   * @param {string} params.pageId - ID of the page that failed to save
   * @param {DesignFile} params.pageFile - The page file that failed
   * @param {Set<string>} params.pagesPendingSave - Set tracking pages pending save
   * @param {SiteDesign} params.siteDesign - Site design entity
   * @param {Object} params.currentPage - Current page entity
   * @param {Function} params.onSelectPage - Function to select a new page
   * @returns {boolean} - Whether cleanup was performed
   */
  static handlePageSaveFailure({
    pageId,
    pageFile,
    pagesPendingSave,
    siteDesign,
    currentPage,
    onSelectPage,
  }) {
    if (!pageFile.isUnableToSave() || !pagesPendingSave.has(pageId)) {
      return false;
    }

    pagesPendingSave.delete(pageId);

    // Remove from site.json since file save failed
    const result = siteDesign.removeItem(pageId);
    if (result.index === -1) {
      return false;
    }

    // If the removed page was the current page, select another page
    if (currentPage.data.get("id") === pageId) {
      this.selectPageAfterRemoval({
        removedPageIndex: result.index,
        siteDesign,
        onSelectPage,
      });
    }

    return true;
  }

  /**
   * Selects an appropriate page after a page is removed
   * @param {Object} params - Selection parameters
   * @param {number} params.removedPageIndex - Index of the removed page
   * @param {SiteDesign} params.siteDesign - Site design entity
   * @param {Function} params.onSelectPage - Function to select a new page
   * @returns {boolean} - Whether a new page was selected
   */
  static selectPageAfterRemoval({ removedPageIndex, siteDesign, onSelectPage }) {
    // Select the previous page
    const selectPageIndex = Math.max(0, removedPageIndex - 1);
    const pageItem = siteDesign.getPageItemByIndex(selectPageIndex);

    if (pageItem) {
      onSelectPage({ page: pageItem });
      return true;
    }

    return false;
  }

  /**
   * Sets up cleanup tracking for a new page
   * @param {Object} params - Setup parameters
   * @param {string} params.pageId - ID of the page
   * @param {DesignFile} params.pageFile - The page file
   * @param {Set<string>} params.pagesPendingSave - Set tracking pages pending save
   * @param {Object} params.store - Reference to the store for cleanup
   */
  static setupPageCleanupTracking({ pageId, pageFile, pagesPendingSave, store }) {
    pagesPendingSave.add(pageId);

    pageFile.once("save", () => {
      pagesPendingSave.delete(pageId);

      // remove cleanup props to break refs
      pageFile._isCleanupCandidate = false;
      pageFile._cleanupPageId = null;
      pageFile._cleanupStore = null;
    });

    // Mark as cleanup candidate BEFORE any saves to prevent error modal
    pageFile._isCleanupCandidate = true;
    pageFile._cleanupPageId = pageId;
    pageFile._cleanupStore = store;
  }
}

export default PageCleanupUtils;
